<script setup lang="ts">
import { useUserManagementStore } from '@/stores/userManagement';
import { Table, UserAvatar, AlertDialog } from '@/components/common';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import { onMounted, ref } from 'vue';
import { UserModel } from '@/models/user';
import Chip from 'primevue/chip';
import { ButtonType, CustomButton } from '@/components';
import { cloneDeep } from 'lodash';
import UserExpandedNew from './UserExpandedNew.vue';
import UserExpanded from './UserExpanded.vue';

const emit = defineEmits(['openInviteDialog']);

const studyUsersStore = useUserManagementStore();

const expandedRows = ref<UserModel[]>([]);

const handleRowToggle = (clickedRow: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === clickedRow.identifier
  );

  if (index === -1) {
    expandedRows.value.push(clickedRow);
  } else {
    expandedRows.value.splice(index, 1);
  }
};

const handleCollapseRequest = (rowToCollapse: UserModel) => {
  const index = expandedRows.value.findIndex(
    row => row.identifier === rowToCollapse.identifier
  );

  if (index !== -1) {
    expandedRows.value.splice(index, 1);
  }
};

const onPerPageRowCountChanged = (perPage: number) => {
  expandedRows.value = [];
};

onMounted(async () => {
  await studyUsersStore.fetchPendingUsers();
});

const inviteColumns: TableColumnModel[] = [
  ...studyUsersStore.tableColumns,
  { id: 3, header: 'Actions', key: 'actions', type: TableColumnType.TEXT, sortable: false }
];

const openAlertDialog = ref(false);
const userToCancel = ref<UserModel>();

const openCancelAlert = (user: UserModel) => {
  userToCancel.value = user;
  openAlertDialog.value = true;
};

const closeCancelAlert = () => {
  openAlertDialog.value = false;
  userToCancel.value = undefined;
};

const confirmCancelInvite = async () => {
  if (userToCancel.value) {
    await studyUsersStore.deleteInvitedUserFromStudy(userToCancel.value);
    closeCancelAlert();
  }
};

const reInviteUser = async (user: UserModel) => {
  await studyUsersStore.reInviteUserToStudy(user);
};

const openUserPermissionDialog = ref<boolean>(false);
const selectedUser = ref<UserModel>();

const userPermissionEditClick = (data: UserModel) => {
  selectedUser.value = cloneDeep(data);
  openCloseUserPermissionDialog();
};

const openCloseUserPermissionDialog = (userModel?: UserModel) => {
  openUserPermissionDialog.value = !openUserPermissionDialog.value;
  const userIndex = studyUsersStore.pendingUsers.findIndex(u => u.identifier === userModel?.identifier);
  if (userIndex !== -1 && userModel) {
    studyUsersStore.pendingUsers[userIndex] = cloneDeep(userModel);
  }
};
</script>

<template>
  <Table :columns="inviteColumns"
    :data-list="studyUsersStore.pendingUsers"
    :loading-state="studyUsersStore.isPendingUsersLoading.isLoading"
    :expandedRows="expandedRows"
    :rows="10"
    @onPerPageRowCountChanged="onPerPageRowCountChanged"
    @onRowClick="handleRowToggle">
    <template #bodyContent="{ data, col }">
      <UserAvatar :avatarUrl="data.avatar"
        :userName="data[col.key]"
        :user-email="data.email"
        :size="44"
        v-if="col.type === TableColumnType.USER"/>

      <div v-if="
          col.key === 'permissionsList' &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        "
        class="permissions-container">
        <span v-for="(permission, index) in data.permissionsList" :key="index">
          <Chip :label="permission" />
        </span>
      </div>

      <div v-else-if="col.key === 'actions'" class="actions-container">
        <CustomButton text="Re-send"
          class="text-nowrap"
          @onClick="reInviteUser(data)"
          :type="ButtonType.POSITIVE"
          :loading="studyUsersStore.isReInvitingUser.isLoading"/>
        <CustomButton text="Cancel"
          :margin-left="8"
          @onClick="openCancelAlert(data)"
          :type="ButtonType.NEGATIVE_OUTLINED"/>
      </div>

      <div v-else-if="
          col.type === TableColumnType.TEXT &&
          !expandedRows.some((row) => row.identifier === data.identifier)
        ">
        {{ data[col.key] }}
      </div>
    </template>
    <template #emptyContent>
      <div class="empty-content-container">
        <div class="empty-content">
          <FontAwesomeIcon :icon="[`fal`, 'mailbox']" class="icon" />
          <h3 class="no-entries-label">No users invited yet</h3>
          <div class="create-context-wrapper">
            <div class="new-entry-text">To invite a user click on</div>
            <CustomButton text="Invite users"
                  :type="ButtonType.POSITIVE"
                  :marginLeft="16"
                  @onClick="emit('openInviteDialog')"/>
          </div>
        </div>
      </div>
    </template>
    <template #expandContent="{ data }">
      <UserExpandedNew :user="data"
        @onUserEdit="userPermissionEditClick"/>
    </template>
  </Table>
  <UserExpanded :visible="openUserPermissionDialog"
    v-if="openUserPermissionDialog && selectedUser"
    :user="selectedUser"
    @onClose="openCloseUserPermissionDialog"/>
  <AlertDialog :visible="openAlertDialog"
    title="Cancel Invite"
    :description="`Are you sure you want to cancel the invite for ${userToCancel?.email}?`"
    :loading="studyUsersStore.isDeletingInvitedUser.isLoading"
    @onClose="closeCancelAlert"
    @onConfirm="confirmCancelInvite"/>
</template>

<style scoped lang="scss">
:deep(.p-row-even) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}
:deep(.p-row-odd) {
  cursor: pointer;
  &:hover {
    background-color: #f2f2f2;
  }
}

.permissions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
}
.create-context-wrapper {
  display: flex;
  align-items: center;
  margin: 16px 0;
}
.empty-content-container {
  .empty-content {
    .icon {
      font-size: 100px;
      margin: 16px 0px;
    }
  }
}
.actions-container {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.text-nowrap {
  white-space: nowrap;
}
</style>
